import React, { useCallback, useEffect, useMemo, useState } from "react";
import { Link, useLocation, useParams } from "react-router-dom";
import { getServiceRequestEmailAnalysisHistory } from "../../api/services/serviceRequestsApi";
import { emailAnalysisStepDomId } from "../../utils/emailAnalysisAnchors";
import {
  budgetAssessmentPillClass,
  budgetStatusDisplayLabel,
  formatEstimatedBudgetRange,
  normalizeBudgetAssessment,
  normalizeProfessionalSuggestion,
  rawClientAnalysisFromEmailAnalysisStep,
} from "../../utils/adminClientAnalysisExtended";
import { offerModifierClass } from "./adminRequestClientEmailBadge";

/**
 * Merges nested `clientAnalysis` or **flat** history-step fields into one view model.
 */
function buildStepViewModel(step) {
  const ca = rawClientAnalysisFromEmailAnalysisStep(step);
  const concernsFromStep = Array.isArray(step?.concerns)
    ? step.concerns.filter((x) => String(x).trim())
    : [];
  const anglesFromStep = Array.isArray(step?.replyAngles)
    ? step.replyAngles.filter((x) => String(x).trim())
    : [];
  const concernsFromCa = Array.isArray(ca?.concerns) ? ca.concerns.filter((x) => String(x).trim()) : [];
  const anglesFromCa = Array.isArray(ca?.replyAngles) ? ca.replyAngles.filter((x) => String(x).trim()) : [];
  return {
    statusRaw: ca?.status ?? step?.status,
    summary: String(ca?.summary ?? step?.description ?? "").trim(),
    offerLabel: ca?.offerLabel ?? step?.offerLabel,
    offerSignal: ca?.offerSignal ?? step?.offerSignal,
    analyzedAt: ca?.analyzedAt ?? step?.analysedAt ?? step?.analyzedAt,
    modelLabel: ca?.modelLabel,
    confidence: ca?.confidence,
    verdict: ca?.verdict,
    sentimentLabel: ca?.sentimentLabel,
    clientIntent: ca?.clientIntent,
    concerns: concernsFromCa.length ? concernsFromCa : concernsFromStep,
    replyAngles: anglesFromCa.length ? anglesFromCa : anglesFromStep,
    errorMessage: ca?.errorMessage,
    analysisSchemaVersion:
      ca?.analysisSchemaVersion != null && !Number.isNaN(Number(ca.analysisSchemaVersion))
        ? Number(ca.analysisSchemaVersion)
        : null,
    professionalSuggestion: normalizeProfessionalSuggestion(ca),
    budgetAssessment: normalizeBudgetAssessment(ca),
  };
}

function normalizeStatusFromString(raw) {
  const s = String(raw ?? "").trim().toLowerCase();
  if (!s) return "pending";
  if (s === "pending" || s === "queued" || s === "running" || s === "in_progress") {
    return "pending";
  }
  if (
    s === "completed" ||
    s === "complete" ||
    s === "success" ||
    s === "succeeded" ||
    s === "ok"
  ) {
    return "completed";
  }
  if (s === "failed" || s === "failure" || s === "error" || s === "errored") {
    return "failed";
  }
  return s;
}

function timelineItemClassFromSt(st) {
  if (st === "failed") {
    return "admin-dash__analysis-timeline-item admin-dash__analysis-timeline-item--fail";
  }
  if (st === "pending") {
    return "admin-dash__analysis-timeline-item admin-dash__analysis-timeline-item--pending";
  }
  if (st === "completed") {
    return "admin-dash__analysis-timeline-item admin-dash__analysis-timeline-item--ok";
  }
  return "admin-dash__analysis-timeline-item";
}

function historyAnalysisStatusBadgeClassFromSt(st) {
  if (st === "completed") {
    return "admin-dash__badge-status admin-dash__badge-status--compact admin-dash__badge-stage admin-dash__badge-stage--confirmed";
  }
  if (st === "failed") {
    return "admin-dash__list-client-email-pill admin-dash__list-client-email-pill--failed";
  }
  if (st === "pending") {
    return "admin-dash__list-client-email-pill admin-dash__list-client-email-pill--pending";
  }
  return "admin-dash__badge-type admin-dash__badge-type--sm";
}

function statusLabelFromSt(st) {
  if (st === "completed") return "Completed";
  if (st === "pending") return "Analysing…";
  if (st === "failed") return "Err";
  return st;
}

function historyOfferPillClassFromVm(vm) {
  const signal = vm.offerSignal != null ? String(vm.offerSignal) : "";
  const mod = signal ? offerModifierClass(signal) : "admin-dash__email-offer-pill--unclear";
  return `admin-dash__email-offer-pill admin-dash__email-offer-pill--table ${mod}`;
}

function formatVerdictLabel(verdict) {
  if (verdict == null || String(verdict).trim() === "") return null;
  return String(verdict)
    .replace(/_/g, " ")
    .replace(/\b\w/g, (c) => c.toUpperCase());
}

function formatConfidence(confidence) {
  if (confidence == null || confidence === "") return null;
  const n = Number(confidence);
  if (Number.isNaN(n)) return String(confidence);
  if (n >= 0 && n <= 1) return `${Math.round(n * 100)}%`;
  return `${n}%`;
}

/** CSS modifier for centered signal values: positivity / risk / neutral. */
function signalValueToneClass(kind, raw) {
  const t = String(raw ?? "").trim().toLowerCase();
  if (kind === "verdict") {
    if (!t) return "admin-dash__email-analysis-signal-chip-v--tone-neutral";
    if (
      t.includes("attention") ||
      t.includes("urgent") ||
      t.includes("risk") ||
      t.includes("warning") ||
      t.includes("caution")
    ) {
      return "admin-dash__email-analysis-signal-chip-v--tone-warn";
    }
    if (
      t.includes("negative") ||
      t.includes("reject") ||
      t.includes("decline") ||
      t.includes("fail") ||
      t.includes("blocked")
    ) {
      return "admin-dash__email-analysis-signal-chip-v--tone-negative";
    }
    if (
      t.includes("ok") ||
      t.includes("clear") ||
      t.includes("positive") ||
      t.includes("good") ||
      t.includes("approve") ||
      t.includes("favorable")
    ) {
      return "admin-dash__email-analysis-signal-chip-v--tone-positive";
    }
    if (t.includes("mixed") || t.includes("neutral")) {
      return "admin-dash__email-analysis-signal-chip-v--tone-mixed";
    }
    return "admin-dash__email-analysis-signal-chip-v--tone-neutral";
  }
  if (kind === "sentiment") {
    if (!t) return "admin-dash__email-analysis-signal-chip-v--tone-neutral";
    if (
      t.includes("positive") ||
      t.includes("bullish") ||
      t.includes("optimistic") ||
      t.includes("favorable") ||
      t.includes("upbeat")
    ) {
      return "admin-dash__email-analysis-signal-chip-v--tone-positive";
    }
    if (
      t.includes("negative") ||
      t.includes("bearish") ||
      t.includes("pessimistic") ||
      t.includes("unfavorable") ||
      t.includes("hostile")
    ) {
      return "admin-dash__email-analysis-signal-chip-v--tone-negative";
    }
    if (t.includes("mixed") || t.includes("ambivalent") || t.includes("conflicted")) {
      return "admin-dash__email-analysis-signal-chip-v--tone-mixed";
    }
    if (t.includes("neutral")) {
      return "admin-dash__email-analysis-signal-chip-v--tone-neutral";
    }
    return "admin-dash__email-analysis-signal-chip-v--tone-neutral";
  }
  if (kind === "confidence") {
    const n = Number(raw);
    if (Number.isNaN(n)) return "admin-dash__email-analysis-signal-chip-v--tone-neutral";
    const p = n >= 0 && n <= 1 ? n : Math.min(1, Math.max(0, n / 100));
    if (p >= 0.75) return "admin-dash__email-analysis-signal-chip-v--tone-positive";
    if (p >= 0.45) return "admin-dash__email-analysis-signal-chip-v--tone-warn";
    return "admin-dash__email-analysis-signal-chip-v--tone-negative";
  }
  return "admin-dash__email-analysis-signal-chip-v--tone-neutral";
}

function capitalizeWords(s) {
  const t = String(s ?? "").trim();
  if (!t) return "";
  return t.replace(/_/g, " ").replace(/\b\w/g, (c) => c.toUpperCase());
}

export default function AdminEmailAnalysisDetailPage() {
  const { id: requestId } = useParams();
  const location = useLocation();
  const [loading, setLoading] = useState(false);
  const [error, setError] = useState(null);
  const [payload, setPayload] = useState(null);
  const [copiedReplyKey, setCopiedReplyKey] = useState(null);

  const copySuggestedReply = useCallback(async (text, key) => {
    if (!text) return;
    try {
      await navigator.clipboard.writeText(text);
      setCopiedReplyKey(key);
      window.setTimeout(() => {
        setCopiedReplyKey((k) => (k === key ? null : k));
      }, 2000);
    } catch (_) {
      /* ignore */
    }
  }, []);

  const load = useCallback(async () => {
    if (!requestId) return;
    setLoading(true);
    setError(null);
    try {
      const { data } = await getServiceRequestEmailAnalysisHistory(requestId);
      setPayload(data || null);
    } catch (e) {
      setPayload(null);
      setError(e?.message || "Could not load analysis history.");
    } finally {
      setLoading(false);
    }
  }, [requestId]);

  useEffect(() => {
    setPayload(null);
    setError(null);
  }, [requestId]);

  useEffect(() => {
    load();
  }, [load]);

  const history = Array.isArray(payload?.history) ? payload.history : [];

  useEffect(() => {
    if (loading || !payload || history.length === 0) return;
    const raw = (location.hash || "").replace(/^#/, "");
    if (!raw || !raw.startsWith("ea-msg-")) return;

    const FLASH_CLASS = "admin-dash__email-analysis-step-card--select-flash";
    let cancelled = false;
    let tScroll = null;
    let tFlash = null;
    let fallbackRemove = null;

    tScroll = window.setTimeout(() => {
      const el = document.getElementById(raw);
      if (!el || cancelled) return;
      el.scrollIntoView({ behavior: "smooth", block: "start" });
    }, 100);

    tFlash = window.setTimeout(() => {
      const el = document.getElementById(raw);
      if (!el || cancelled) return;

      const clearFlash = () => {
        el.classList.remove(FLASH_CLASS);
      };

      const onAnimEnd = (e) => {
        const name = String(e.animationName || "");
        if (!name.includes("admin-dash-ea-step-select")) return;
        window.clearTimeout(fallbackRemove);
        el.removeEventListener("animationend", onAnimEnd);
        clearFlash();
      };

      el.classList.add(FLASH_CLASS);
      el.addEventListener("animationend", onAnimEnd);
      fallbackRemove = window.setTimeout(() => {
        el.removeEventListener("animationend", onAnimEnd);
        clearFlash();
      }, 1600);
    }, 700);

    return () => {
      cancelled = true;
      if (tScroll) window.clearTimeout(tScroll);
      if (tFlash) window.clearTimeout(tFlash);
      if (fallbackRemove) window.clearTimeout(fallbackRemove);
      const el = document.getElementById(raw);
      if (el) el.classList.remove(FLASH_CLASS);
    };
  }, [loading, payload, history.length, location.hash]);
  const client = payload?.client || {};
  const resolvedRequestId = payload?.requestId || requestId;
  const analysed =
    typeof payload?.numberOfEmailsAnalysed === "number" ? payload.numberOfEmailsAnalysed : 0;
  const totalInbound =
    typeof payload?.totalInboundEmails === "number" ? payload.totalInboundEmails : 0;

  const progressPct = useMemo(() => {
    if (totalInbound <= 0) return 0;
    return Math.min(100, Math.round((analysed / totalInbound) * 100));
  }, [analysed, totalInbound]);

  return (
    <div className="admin-dash__page admin-dash__page--detail">
      <header className="admin-dash__page-head">
        <Link className="admin-dash__back" to="/admin/dashboard/email-analysis">
          ← Email analyser
        </Link>
        <p className="admin-dash__page-eyebrow">Analysis details</p>
        <h1 className="admin-dash__h1">Inbound reply analysis</h1>
        <p className="admin-dash__lede">
          Full AI readout per inbound message: summary, stance, intent, concerns, and suggested reply angles.
        </p>
      </header>

      {error ? (
        <div className="admin-dash__error-banner" role="alert">
          {error}{" "}
          <button type="button" className="admin-dash__btn-text" onClick={load}>
            Retry
          </button>
        </div>
      ) : null}

      {loading && !payload ? (
        <div className="admin-dash__analysis-pending" role="status">
          <span className="admin-dash__analysis-spinner" aria-hidden />
          Loading analysis history…
        </div>
      ) : null}

      {payload ? (
        <div className="admin-dash__analysis-studio admin-dash__email-analysis-hero">
          <div className="admin-dash__analysis-studio-inner">
            <div className="admin-dash__analysis-studio-head admin-dash__email-analysis-hero-head">
              <div className="admin-dash__analysis-studio-brand">
                <div className="admin-dash__analysis-studio-icon-wrap" aria-hidden>
                  <svg width="22" height="22" viewBox="0 0 24 24" fill="none" stroke="currentColor">
                    <path
                      strokeLinecap="round"
                      strokeLinejoin="round"
                      strokeWidth="2"
                      d="M3 8l7.89 5.26a2 2 0 002.22 0L21 8M5 19h14a2 2 0 002-2V7a2 2 0 00-2-2H5a2 2 0 00-2 2v10a2 2 0 002 2z"
                    />
                  </svg>
                </div>
                <div>
                  <p className="admin-dash__analysis-studio-eyebrow">Client</p>
                  <h2 className="admin-dash__analysis-studio-title admin-dash__email-analysis-title">
                    {client.name?.trim() || "Unknown client"}
                  </h2>
                  <p className="admin-dash__analysis-studio-lede admin-dash__email-analysis-client-meta">
                    {client.email ? <span>{client.email}</span> : null}
                    {client.email && client.companyName ? <span aria-hidden> · </span> : null}
                    {client.companyName ? <span>{client.companyName}</span> : null}
                  </p>
                </div>
              </div>
              <div className="admin-dash__analysis-studio-toolbar admin-dash__email-analysis-toolbar">
                <span className="admin-dash__email-analysis-id" title={resolvedRequestId}>
                  Request <code className="admin-dash__code-inline">{resolvedRequestId}</code>
                </span>
                <Link
                  className="admin-dash__btn-primary admin-dash__btn-primary--compact"
                  to={`/admin/dashboard/requests/${resolvedRequestId}#request-email-thread`}
                >
                  Open email thread
                </Link>
              </div>
            </div>

            <div className="admin-dash__email-analysis-stats" aria-label="Analysis coverage">
              <div className="admin-dash__email-analysis-stat">
                <span className="admin-dash__email-analysis-stat-k">Analysed</span>
                <span className="admin-dash__email-analysis-stat-v">{analysed}</span>
              </div>
              <div className="admin-dash__email-analysis-stat">
                <span className="admin-dash__email-analysis-stat-k">Total inbound</span>
                <span className="admin-dash__email-analysis-stat-v">{totalInbound}</span>
              </div>
              <div className="admin-dash__email-analysis-stat">
                <span className="admin-dash__email-analysis-stat-k">Steps</span>
                <span className="admin-dash__email-analysis-stat-v">{history.length}</span>
              </div>
            </div>

            <div className="admin-dash__email-analysis-progress-wrap">
              <div className="admin-dash__email-analysis-progress-label">
                <span>Coverage</span>
                <span className="admin-dash__email-analysis-progress-fraction">
                  {analysed} / {totalInbound} inbound
                </span>
              </div>
              <div
                className="admin-dash__email-analysis-progress"
                role="progressbar"
                aria-valuenow={progressPct}
                aria-valuemin={0}
                aria-valuemax={100}
                aria-label={`${progressPct} percent of inbound emails analysed`}
              >
                <div
                  className="admin-dash__email-analysis-progress-bar"
                  style={{ width: `${progressPct}%` }}
                />
              </div>
            </div>
          </div>
        </div>
      ) : null}

      <div className="admin-dash__analysis-history admin-dash__analysis-history--timeline admin-dash__email-analysis-steps">
        <div className="admin-dash__email-analysis-timeline-panel">
          <div className="admin-dash__analysis-history-head">
            <h3 className="admin-dash__analysis-history-title">Inbound analysis timeline</h3>
            <p className="admin-dash__analysis-history-sub">
              Oldest first — step 1 is the earliest inbound message. Each card is one AI pass on that message.
              Professional fit and budget-aware suggested reply appear when the API includes{" "}
              <code className="admin-dash__code-inline">professionalSuggestion</code> and{" "}
              <code className="admin-dash__code-inline">budgetAssessment</code> (nested under{" "}
              <code className="admin-dash__code-inline">clientAnalysis</code> or on the same object as the step).
            </p>
          </div>

          {loading && payload ? (
            <div className="admin-dash__analysis-pending" role="status">
              <span className="admin-dash__analysis-spinner" aria-hidden />
              Refreshing steps…
            </div>
          ) : null}

          {!loading && payload && history.length === 0 ? (
            <p className="admin-dash__email-analysis-empty">No analysis steps returned for this request.</p>
          ) : !loading && payload && history.length > 0 ? (
            <ol className="admin-dash__analysis-timeline">
            {history.map((step) => {
              const vm = buildStepViewModel(step);
              const st = normalizeStatusFromString(vm.statusRaw);
              const label = statusLabelFromSt(st);
              const confidenceStr = formatConfidence(vm.confidence);
              const verdictLabel = formatVerdictLabel(vm.verdict);
              const anchorId = emailAnalysisStepDomId(step.emailMessageId, step.step);
              const hashRaw = (location.hash || "").replace(/^#/, "");
              const isHashSelected = Boolean(hashRaw && hashRaw === anchorId);

              return (
                <li
                  key={step.emailMessageId ? `${step.emailMessageId}:${step.step}` : `step-${step.step}`}
                  className={timelineItemClassFromSt(st)}
                >
                  <span className="admin-dash__analysis-timeline-node" aria-hidden />
                  <article
                    id={anchorId}
                    className={`admin-dash__analysis-timeline-card admin-dash__email-analysis-step-card${
                      isHashSelected ? " admin-dash__email-analysis-step-card--selected" : ""
                    }`}
                  >
                    <header className="admin-dash__email-analysis-step-card-head">
                      <div className="admin-dash__email-analysis-step-card-head-main">
                        {step.replyAt ? (
                          <time
                            className="admin-dash__email-analysis-step-inbound-time"
                            dateTime={step.replyAt}
                          >
                            Inbound · {new Date(step.replyAt).toLocaleString()}
                          </time>
                        ) : (
                          <span className="admin-dash__email-analysis-step-inbound-time">
                            Inbound time unknown
                          </span>
                        )}
                        {vm.analyzedAt ? (
                          <span className="admin-dash__email-analysis-step-analysed-inline">
                            Analysed ·{" "}
                            <time dateTime={vm.analyzedAt}>
                              {new Date(vm.analyzedAt).toLocaleString()}
                            </time>
                          </span>
                        ) : null}
                      </div>
                      <div className="admin-dash__td-badges admin-dash__email-analysis-step-badges">
                        <span
                          className="admin-dash__badge-type admin-dash__badge-type--sm"
                          title={`Inbound step ${step.step}${totalInbound > 0 ? ` of ${totalInbound}` : ""}`}
                        >
                          Step {step.step}
                          {totalInbound > 0 ? ` / ${totalInbound}` : ""}
                        </span>
                        {vm.offerLabel ? (
                          <span className={historyOfferPillClassFromVm(vm)} title={vm.offerLabel}>
                            {vm.offerLabel}
                          </span>
                        ) : null}
                        <span className={historyAnalysisStatusBadgeClassFromSt(st)} title={label}>
                          {label}
                        </span>
                      </div>
                    </header>

                    {st === "failed" && vm.errorMessage ? (
                      <div className="admin-dash__email-analysis-step-error" role="alert">
                        <strong>Analysis error</strong>
                        <p>{vm.errorMessage}</p>
                      </div>
                    ) : null}

                    {vm.summary ? (
                      <section
                        className="admin-dash__email-analysis-section admin-dash__email-analysis-section--summary"
                        aria-labelledby={`summary-${step.step}-${step.emailMessageId}`}
                      >
                        <h4 id={`summary-${step.step}-${step.emailMessageId}`} className="admin-dash__email-analysis-section-title">
                          Summary
                        </h4>
                        <p className="admin-dash__email-analysis-summary-prose">{vm.summary}</p>
                      </section>
                    ) : st === "pending" ? (
                      <p className="admin-dash__email-analysis-waiting">Waiting for analysis…</p>
                    ) : null}

                    {(verdictLabel ||
                      vm.sentimentLabel ||
                      vm.clientIntent ||
                      confidenceStr ||
                      vm.modelLabel) ? (
                      <section
                        className="admin-dash__email-analysis-section admin-dash__email-analysis-section--signals"
                        aria-label="Analysis metadata"
                      >
                        <h4 className="admin-dash__email-analysis-section-title">Signals</h4>
                        <div className="admin-dash__email-analysis-signals-board">
                          {verdictLabel || vm.sentimentLabel || confidenceStr ? (
                            <div className="admin-dash__email-analysis-signals-chips" role="list">
                              {verdictLabel ? (
                                <div className="admin-dash__email-analysis-signal-chip" role="listitem">
                                  <span className="admin-dash__email-analysis-signal-chip-k">Verdict</span>
                                  <span
                                    className={`admin-dash__email-analysis-signal-chip-v ${signalValueToneClass(
                                      "verdict",
                                      vm.verdict ?? verdictLabel
                                    )}`}
                                  >
                                    {verdictLabel}
                                  </span>
                                </div>
                              ) : null}
                              {vm.sentimentLabel ? (
                                <div className="admin-dash__email-analysis-signal-chip" role="listitem">
                                  <span className="admin-dash__email-analysis-signal-chip-k">Sentiment</span>
                                  <span
                                    className={`admin-dash__email-analysis-signal-chip-v ${signalValueToneClass(
                                      "sentiment",
                                      vm.sentimentLabel
                                    )}`}
                                  >
                                    {vm.sentimentLabel}
                                  </span>
                                </div>
                              ) : null}
                              {confidenceStr ? (
                                <div className="admin-dash__email-analysis-signal-chip" role="listitem">
                                  <span className="admin-dash__email-analysis-signal-chip-k">Confidence</span>
                                  <span
                                    className={`admin-dash__email-analysis-signal-chip-v admin-dash__email-analysis-signal-chip-v--num ${signalValueToneClass(
                                      "confidence",
                                      vm.confidence
                                    )}`}
                                  >
                                    {confidenceStr}
                                  </span>
                                </div>
                              ) : null}
                            </div>
                          ) : null}
                          {vm.clientIntent ? (
                            <div className="admin-dash__email-analysis-signals-narrative">
                              <span className="admin-dash__email-analysis-signals-narrative-k">Client intent</span>
                              <p className="admin-dash__email-analysis-signals-narrative-p">{vm.clientIntent}</p>
                            </div>
                          ) : null}
                          {vm.modelLabel ? (
                            <div className="admin-dash__email-analysis-signals-model">
                              <span className="admin-dash__email-analysis-signals-model-k">Model</span>
                              <code className="admin-dash__email-analysis-model-code">{vm.modelLabel}</code>
                            </div>
                          ) : null}
                        </div>
                      </section>
                    ) : null}

                    {st === "completed" && vm.professionalSuggestion ? (
                      <section
                        className="admin-dash__email-analysis-section admin-dash__email-analysis-section--pro-fit"
                        aria-labelledby={`pro-${step.step}-${step.emailMessageId}`}
                      >
                        <h4
                          id={`pro-${step.step}-${step.emailMessageId}`}
                          className="admin-dash__email-analysis-section-title"
                        >
                          Professional fit
                        </h4>
                        {vm.professionalSuggestion.requiredProfessionalLevel ? (
                          <p className="admin-dash__email-analysis-pro-level">
                            <span className="admin-dash__email-analysis-pro-level-k">Level</span>{" "}
                            {capitalizeWords(vm.professionalSuggestion.requiredProfessionalLevel)}
                          </p>
                        ) : null}
                        {vm.professionalSuggestion.recommendedService ? (
                          <p className="admin-dash__email-analysis-pro-line">
                            <span className="admin-dash__email-analysis-pro-k">Recommended</span>{" "}
                            {vm.professionalSuggestion.recommendedService}
                          </p>
                        ) : null}
                        {vm.professionalSuggestion.whyThisService ? (
                          <p className="admin-dash__email-analysis-pro-body">{vm.professionalSuggestion.whyThisService}</p>
                        ) : null}
                        {vm.professionalSuggestion.requiredSkills.length > 0 ? (
                          <ul className="admin-dash__email-analysis-pro-skills">
                            {vm.professionalSuggestion.requiredSkills.map((sk, i) => (
                              <li key={i}>{sk}</li>
                            ))}
                          </ul>
                        ) : null}
                        {vm.professionalSuggestion.deliveryApproach ? (
                          <p className="admin-dash__email-analysis-pro-line">
                            <span className="admin-dash__email-analysis-pro-k">Approach</span>{" "}
                            {vm.professionalSuggestion.deliveryApproach}
                          </p>
                        ) : null}
                        {vm.professionalSuggestion.riskIfUnderScoped.length > 0 ? (
                          <div className="admin-dash__email-analysis-pro-risks">
                            <span className="admin-dash__email-analysis-pro-k">If under-scoped</span>
                            <ul>
                              {vm.professionalSuggestion.riskIfUnderScoped.map((line, i) => (
                                <li key={i}>{line}</li>
                              ))}
                            </ul>
                          </div>
                        ) : null}
                        {vm.professionalSuggestion.suggestedNextQuestion ? (
                          <p className="admin-dash__email-analysis-pro-ask">
                            <span className="admin-dash__email-analysis-pro-k">Ask next</span>{" "}
                            {vm.professionalSuggestion.suggestedNextQuestion}
                          </p>
                        ) : null}
                      </section>
                    ) : null}

                    {st === "completed" && vm.budgetAssessment ? (
                      <section
                        className="admin-dash__email-analysis-section admin-dash__email-analysis-section--budget"
                        aria-labelledby={`budget-${step.step}-${step.emailMessageId}`}
                      >
                        <h4
                          id={`budget-${step.step}-${step.emailMessageId}`}
                          className="admin-dash__email-analysis-section-title"
                        >
                          Budget & reply
                        </h4>
                        <div className="admin-dash__email-analysis-budget-head">
                          {vm.budgetAssessment.status ? (
                            <span
                              className={`admin-dash__email-analysis-budget-pill ${budgetAssessmentPillClass(
                                vm.budgetAssessment.status
                              )}`}
                            >
                              {budgetStatusDisplayLabel(vm.budgetAssessment.status)}
                            </span>
                          ) : null}
                          {vm.budgetAssessment.fitScore != null ? (
                            <span className="admin-dash__email-analysis-budget-fit">
                              Fit · {Math.round(vm.budgetAssessment.fitScore)}/100
                            </span>
                          ) : null}
                        </div>
                        {vm.budgetAssessment.clientBudgetMentioned ? (
                          <p className="admin-dash__email-analysis-budget-line">
                            <span className="admin-dash__email-analysis-pro-k">Client said</span>{" "}
                            {vm.budgetAssessment.clientBudgetMentioned}
                          </p>
                        ) : null}
                        {formatEstimatedBudgetRange(vm.budgetAssessment) ? (
                          <p className="admin-dash__email-analysis-budget-line">
                            <span className="admin-dash__email-analysis-pro-k">Estimate band</span>{" "}
                            {formatEstimatedBudgetRange(vm.budgetAssessment)}
                          </p>
                        ) : null}
                        {vm.budgetAssessment.gapNote ? (
                          <p className="admin-dash__email-analysis-budget-body">{vm.budgetAssessment.gapNote}</p>
                        ) : null}
                        {vm.budgetAssessment.negotiationAdvice.length > 0 ? (
                          <ul className="admin-dash__email-analysis-budget-advice">
                            {vm.budgetAssessment.negotiationAdvice.map((line, i) => (
                              <li key={i}>{line}</li>
                            ))}
                          </ul>
                        ) : null}
                        {vm.budgetAssessment.suggestedClientReply ? (
                          <div className="admin-dash__email-analysis-suggested-reply">
                            <div className="admin-dash__email-analysis-suggested-reply-head">
                              <span className="admin-dash__email-analysis-pro-k">Suggested reply</span>
                              <button
                                type="button"
                                className="admin-dash__btn-text admin-dash__btn-text--small"
                                onClick={() =>
                                  copySuggestedReply(
                                    vm.budgetAssessment.suggestedClientReply,
                                    `br-${step.step}-${step.emailMessageId}`
                                  )
                                }
                              >
                                {copiedReplyKey === `br-${step.step}-${step.emailMessageId}`
                                  ? "Copied"
                                  : "Copy"}
                              </button>
                            </div>
                            <pre className="admin-dash__email-analysis-suggested-reply-text">
                              {vm.budgetAssessment.suggestedClientReply}
                            </pre>
                          </div>
                        ) : null}
                      </section>
                    ) : null}

                    {vm.concerns.length > 0 || vm.replyAngles.length > 0 ? (
                      <div className="admin-dash__email-analysis-insight-flow">
                        {vm.concerns.length > 0 ? (
                          <section
                            className="admin-dash__email-analysis-section admin-dash__email-analysis-section--concerns"
                            aria-labelledby={`concerns-${step.step}-${step.emailMessageId}`}
                          >
                            <h4
                              id={`concerns-${step.step}-${step.emailMessageId}`}
                              className="admin-dash__email-analysis-section-title"
                            >
                              Concerns
                            </h4>
                            <ul className="admin-dash__email-analysis-risk-stack">
                              {vm.concerns.map((line, i) => (
                                <li key={i} className="admin-dash__email-analysis-risk-line">
                                  <span className="admin-dash__email-analysis-risk-line-text">{line}</span>
                                </li>
                              ))}
                            </ul>
                          </section>
                        ) : null}
                        {vm.replyAngles.length > 0 ? (
                          <section
                            className="admin-dash__email-analysis-section admin-dash__email-analysis-section--angles"
                            aria-labelledby={`angles-${step.step}-${step.emailMessageId}`}
                          >
                            <h4
                              id={`angles-${step.step}-${step.emailMessageId}`}
                              className="admin-dash__email-analysis-section-title"
                            >
                              Suggested reply angles
                            </h4>
                            <ul className="admin-dash__email-analysis-reply-ladder">
                              {vm.replyAngles.map((line, i) => (
                                <li key={i} className="admin-dash__email-analysis-reply-step">
                                  <span className="admin-dash__email-analysis-reply-step-body">{line}</span>
                                </li>
                              ))}
                            </ul>
                          </section>
                        ) : null}
                      </div>
                    ) : null}

                    {step.emailMessageId ? (
                      <footer className="admin-dash__email-analysis-step-footer">
                        Message ID{" "}
                        <code className="admin-dash__code-inline">{step.emailMessageId}</code>
                      </footer>
                    ) : null}
                  </article>
                </li>
              );
            })}
            </ol>
          ) : null}
        </div>
      </div>
    </div>
  );
}
